// Card-eligible powerup types in their default priority order (highest first).
// This order decides the winner among the near-highest contenders (see
// `selectCardPowerUp`).
export const cardPowerUpOrder = ['plane', 'battery', 'shield', 'coin'] as const

export type CardPowerUpType = (typeof cardPowerUpOrder)[number]

// Maximum number of each item collectable in a single run. Used to turn raw
// collection counts into a comparable "completion" percentage per item.
export const maxCollectablePerRun: Record<CardPowerUpType, number> = {
    coin: 114,
    shield: 5,
    battery: 5,
    plane: 3,
}

// Applied to the computed card percentage for each type. Coins get a slight
// boost so the treasure-hunter card is a little easier to reach.
export const cardPercentageFactor: Record<CardPowerUpType, number> = {
    coin: 1.1,
    shield: 1,
    battery: 1,
    plane: 1,
}

// Items whose percentage is within this many points below the highest
// percentage are treated as contenders; the default priority order then
// decides the winner among them.
export const cardContenderThreshold = 5

/**
 * Picks the card powerup type for the end card based on how close each item got
 * to its per-run maximum (as a percentage).
 *
 * The highest percentage is found, then every item that is less than
 * `cardContenderThreshold` percentage points below it forms the contender
 * group. Within that group, the default order (plane > battery > shield > coin)
 * decides the winner. With nothing collected all items tie at 0%, so plane wins
 * by default.
 */
export function selectCardPowerUp(
    collectedCounts: Record<CardPowerUpType, number>,
): CardPowerUpType {
    const percentages = cardPowerUpOrder.map((type) => ({
        type,
        percentage:
            maxCollectablePerRun[type] > 0
                ? (collectedCounts[type] / maxCollectablePerRun[type]) *
                  100 *
                  cardPercentageFactor[type]
                : 0,
    }))

    const highestPercentage = Math.max(...percentages.map((p) => p.percentage))

    const contenders = new Set(
        percentages
            .filter((p) => highestPercentage - p.percentage < cardContenderThreshold)
            .map((p) => p.type),
    )

    // `cardPowerUpOrder` is highest priority first, so the first contender in
    // that order is the winner.
    return cardPowerUpOrder.find((type) => contenders.has(type)) ?? cardPowerUpOrder[0]
}
